import React from 'react'
import { Button, Form, Icon, Loader, Message } from 'semantic-ui-react'

import { Recruiter, recruiterSchema } from '../../types'
import { useTalentApi } from '../../store/mainContext'
import { validateResponse } from '../../utils'

type RecruiterFormState = {
  name: string
  email: string
  paymentTerm: string
  feeCap: string
  notes: string
}

interface RecruiterSidePanelProps {
  open: boolean
  recruiterId?: string
  onClose(): void
  onSaved(): void
  onDeleted(): void
}

const createBlankFormState = (): RecruiterFormState => ({
  name: '',
  email: '',
  paymentTerm: '',
  feeCap: '',
  notes: '',
})

const createFormStateFromRecruiter = (
  recruiter: Recruiter,
): RecruiterFormState => ({
  name: recruiter.name ?? '',
  email: recruiter.email ?? '',
  paymentTerm:
    recruiter.paymentTerm != null ? String(recruiter.paymentTerm) : '',
  feeCap: recruiter.feeCap != null ? String(recruiter.feeCap) : '',
  notes: recruiter.notes ?? '',
})

const editableFields: Array<keyof RecruiterFormState> = [
  'name',
  'email',
  'paymentTerm',
  'feeCap',
  'notes',
]

const sanitizeString = (value: string): string => value.trim()

export default function RecruiterSidePanel({
  open,
  recruiterId,
  onClose,
  onSaved,
  onDeleted,
}: RecruiterSidePanelProps) {
  const api = useTalentApi()

  const isNew = !recruiterId

  const [formState, setFormState] = React.useState<RecruiterFormState | null>(
    null,
  )
  const [baseline, setBaseline] = React.useState<RecruiterFormState | null>(
    null,
  )
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setFormState(null)
      setBaseline(null)
      setError(null)
      setIsLoading(false)
      setIsSaving(false)
      setIsDeleting(false)
      return
    }

    if (!recruiterId) {
      const blank = createBlankFormState()
      setFormState(blank)
      setBaseline(blank)
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    api
      .get(`recruiters/${recruiterId}`)
      .then(validateResponse(recruiterSchema))
      .then((recruiter) => {
        const state = createFormStateFromRecruiter(recruiter)
        setFormState(state)
        setBaseline(state)
      })
      .catch((err) => {
        console.error(err)
        setError('Unable to load recruiter details.')
      })
      .finally(() => setIsLoading(false))
  }, [api, open, recruiterId])

  const isDirty = React.useMemo(() => {
    if (!baseline || !formState) return false
    return editableFields.some((field) => baseline[field] !== formState[field])
  }, [baseline, formState])

  const canSave = Boolean(formState) && (isNew || isDirty)

  const handleFieldChange = <Key extends keyof RecruiterFormState>(
    field: Key,
    value: RecruiterFormState[Key],
  ) => {
    setFormState((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleSave = async () => {
    if (!formState) return

    const parsedPaymentTerm = formState.paymentTerm.trim()
      ? Number.parseInt(formState.paymentTerm, 10)
      : null
    const parsedFeeCap = formState.feeCap.trim()
      ? Number.parseFloat(formState.feeCap)
      : null

    const payload = {
      name: sanitizeString(formState.name),
      email: sanitizeString(formState.email),
      paymentTerm:
        parsedPaymentTerm !== null && !Number.isNaN(parsedPaymentTerm)
          ? parsedPaymentTerm
          : null,
      feeCap:
        parsedFeeCap !== null && !Number.isNaN(parsedFeeCap)
          ? parsedFeeCap
          : null,
      notes: sanitizeString(formState.notes),
    }

    if (!payload.name || !payload.email) {
      setError('Name and email are required.')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (isNew) {
        await api.post('recruiters', payload)
      } else {
        await api.patch(`recruiters/${recruiterId}`, payload)
      }
      onSaved()
    } catch (err) {
      console.error(err)
      setError('Failed to save recruiter changes.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!recruiterId) return

    const confirmed = window.confirm(
      'Are you sure you want to delete this recruiter? This action cannot be undone.',
    )

    if (!confirmed) return

    setIsDeleting(true)
    setError(null)

    try {
      await api.delete(`recruiters/${recruiterId}`)
      onDeleted()
    } catch (err) {
      console.error(err)
      setError('Failed to delete recruiter.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (isSaving || isDeleting || !canSave) return

    handleSave()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/60"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <Message negative>
              <Message.Header>Something went wrong</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div className="flex justify-between">
            <h1 className="text-xl font-semibold text-slate-900">
              {isNew ? 'Add Recruiter' : 'Edit Recruiter'}
            </h1>
            <Button
              icon
              basic
              size="small"
              aria-label="Close recruiter panel"
              onClick={onClose}
              disabled={isSaving || isDeleting}
            >
              <Icon name="close" />
            </Button>
          </div>

          {isLoading || !formState ? (
            <div className="flex h-full items-center justify-center py-10">
              <Loader active inline="centered" />
            </div>
          ) : (
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              loading={isSaving}
              className="flex h-full flex-col"
            >
              <div className="flex flex-col gap-8">
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Recruiter Details
                  </h2>

                  <Form.Input
                    label="Name"
                    value={formState.name}
                    onChange={(_, data) =>
                      handleFieldChange('name', String(data.value ?? ''))
                    }
                    required
                  />

                  <Form.Input
                    label="Email"
                    type="email"
                    value={formState.email}
                    onChange={(_, data) =>
                      handleFieldChange('email', String(data.value ?? ''))
                    }
                    required
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <Form.Input
                      label="Payment Term (days)"
                      type="number"
                      min="0"
                      step="1"
                      value={formState.paymentTerm}
                      onChange={(_, data) =>
                        handleFieldChange(
                          'paymentTerm',
                          String(data.value ?? ''),
                        )
                      }
                    />

                    <Form.Input
                      label="Fee Cap (USD)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formState.feeCap}
                      onChange={(_, data) =>
                        handleFieldChange('feeCap', String(data.value ?? ''))
                      }
                    />
                  </div>

                  <Form.TextArea
                    label="Notes"
                    rows={8}
                    value={formState.notes}
                    onChange={(_, data) =>
                      handleFieldChange('notes', String(data.value ?? ''))
                    }
                  />
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between border-t pt-4">
                <div>
                  {!isNew && (
                    <Button
                      type="button"
                      basic
                      color="red"
                      onClick={handleDelete}
                      disabled={isSaving || isDeleting}
                      loading={isDeleting}
                    >
                      Delete
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    basic
                    onClick={onClose}
                    disabled={isSaving || isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    primary
                    disabled={!canSave || isSaving || isDeleting}
                    loading={isSaving}
                  >
                    {isNew ? 'Create Recruiter' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  )
}
